import React, { useState } from 'react';
import { Form, Row, Col, Input, Button } from 'reactstrap';
import Select from 'react-select';
import { ModalButtonBuilder, RegisteredInputsBuilder, ToastBuilder, ButtonBuilder } from '../../../../../../../../../GlobalComponents/Builders';
import { parseJSONSafe } from '../../../../../../../../../GlobalComponents/Helpers';
import { useForm } from 'react-hook-form';

/**
 * SyncFilterBuilder — Filter builder for sync jobs.
 *
 * Outputs a JSON object with:
 *   - conditions: the filter string with sync placeholders ({{startDate...}}, {{dateSearch}}, etc.)
 *   - args: extra key/value pairs passed directly to the API client (e.g., include: "requester")
 *
 * For legacy plain-string filters, the builder shows a read-only notice.
 */

const PLACEHOLDER_HINTS = [
    { placeholder: '{{startDate2026-01-01}}', description: 'Fallback start date for first sync' },
    { placeholder: '{{dateSearch}}', description: 'Halo date search param' },
    { placeholder: '{{idFilter}}', description: 'Halo ID filter param' },
    { placeholder: '{{notApiMember}}', description: 'ServiceNow: exclude API user updates' },
    { placeholder: '{{notSystem}}', description: 'ServiceNow: exclude system updates' },
    { placeholder: '{{date}}', description: 'Replaced with last processed timestamp' },
];

const SyncFilterBuilder = ({ getCurrentFilter, onApply, readOnly }) => {

    const { register, handleSubmit, errors = {}, control, setValue } = useForm();
    const [apiArgs, setApiArgs] = useState([]);
    const [fields, setFields] = useState('');
    const [options, setOptions] = useState([]);
    const [isLegacyString, setIsLegacyString] = useState(false);

    const selectStyles = {
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    };

    const loadFilterToForm = () => {
        const current = typeof getCurrentFilter === 'function' ? getCurrentFilter() : getCurrentFilter;
        const cleaned = current ? (typeof current === 'string' ? current.replace(/^#/, '').trim() : current) : '';

        if (!cleaned) {
            setIsLegacyString(false);
            setApiArgs([]);
            setFields('');
            setOptions([]);
            setValue('syncConditions', '');
            return;
        }

        const parsed = parseJSONSafe(cleaned);

        // If it's a JSON object with 'conditions' key — new format
        if (parsed && typeof parsed === 'object' && 'conditions' in parsed) {
            setIsLegacyString(false);
            setValue('syncConditions', parsed.conditions || '');
            const argsObj = parsed.args || {};
            setApiArgs(Object.entries(argsObj).map(([key, value]) => ({ key, value: typeof value === 'object' ? JSON.stringify(value) : String(value) })));
            setFields(Array.isArray(parsed.fields) ? parsed.fields.join(', ') : (parsed.fields || ''));
            setOptions(Array.isArray(parsed.options) ? parsed.options : (parsed.options ? [parsed.options] : []));
            return;
        }

        // Otherwise it's a legacy plain string — load it into conditions, no args
        if (typeof cleaned === 'string' && cleaned) {
            setIsLegacyString(false);
            setValue('syncConditions', cleaned);
            setApiArgs([]);
            setFields('');
            setOptions([]);
            return;
        }

        setIsLegacyString(true);
        setApiArgs([]);
    };

    const addApiArg = () => setApiArgs(prev => [...prev, { key: '', value: '' }]);

    const updateApiArg = (index, field, value) => {
        setApiArgs(prev => prev.map((a, i) => i === index ? { ...a, [field]: value } : a));
    };

    const removeApiArg = (index) => setApiArgs(prev => prev.filter((_, i) => i !== index));

    const onSubmit = (formData) => {
        try {
            const conditions = (formData.syncConditions || '').trim();
            const validArgs = apiArgs.filter(a => a.key.trim());
            const trimmedFields = fields.trim();

            // If no args, no fields, no options — output just the conditions string (backwards compatible)
            if (validArgs.length === 0 && !trimmedFields && options.length === 0 && conditions) {
                onApply?.(conditions);
                return;
            }

            // Build JSON object format
            const result = {};
            if (conditions) result.conditions = conditions;

            if (validArgs.length > 0) {
                result.args = {};
                for (const arg of validArgs) {
                    let parsedValue = arg.value;
                    try {
                        const p = JSON.parse(arg.value);
                        parsedValue = p;
                    } catch {
                        // Keep as string
                    }
                    result.args[arg.key.trim()] = parsedValue;
                }
            }

            if (trimmedFields) {
                result.fields = trimmedFields.split(',').map(f => f.trim()).filter(Boolean);
            }

            if (options.length > 0) {
                result.options = options;
            }

            // If we only have conditions and nothing else, keep it as a plain string
            if (Object.keys(result).length === 1 && result.conditions) {
                onApply?.(result.conditions);
            } else {
                onApply?.(result);
            }
        } catch (e) {
            console.warn('[SyncFilterBuilder] Submit error:', e);
        }
    };

    return (
        <ModalButtonBuilder
            title='Sync Filter'
            type='button'
            className='btn-info btn-sm'
            buttonStyle={{ minWidth: 110 }}
            modalTitle='Sync Filter Builder'
            formId='sync-filter-builder-form'
            style={{ width: 650, maxWidth: 650 }}
            onSubmit={readOnly ? false : 'Apply Filter'}
            onCancelLabel={readOnly ? 'Close' : 'Cancel'}
            onClick={loadFilterToForm}
            body={
                <Form id='sync-filter-builder-form' onSubmit={(e) => { e.stopPropagation(); handleSubmit((data) => {
                    onSubmit(data);
                    ToastBuilder('info', "Don't forget to Submit to save", {}, 5000);
                })(e); }}>

                    {/* Conditions */}
                    <div className='section-group-muted' style={{ borderRadius: 6, padding: '10px 14px' }}>
                        <div className='d-flex justify-content-between align-items-center' style={{ marginBottom: -12 }}>
                            <span className='section-group-title' style={{ fontWeight: 600, fontSize: 13 }}>Sync Filter Conditions</span>
                        </div>
                        <RegisteredInputsBuilder
                            control={control}
                            registrator={register}
                            registratorErrors={errors}
                            readOnly={readOnly}
                            inputs={[{
                                label: null, labelSize: 0, inputSize: 12, size: 12,
                                type: 'textarea', name: 'syncConditions',
                                placeholder: "board/id IN (199,200) AND company/id = 20558 AND _info/dateEntered > [2026-01-01]",
                                style: { height: 100, fontSize: 12, fontFamily: 'monospace' },
                                settings: { handleTabs: true, resize: true, compact: true },
                            }]}
                        />
                        <details style={{ fontSize: 11, color: '#666', marginTop: 4 }}>
                            <summary style={{ cursor: 'pointer' }}>Available Placeholders</summary>
                            <ul style={{ paddingLeft: 16, marginTop: 4, marginBottom: 0 }}>
                                {PLACEHOLDER_HINTS.map((h, i) => (
                                    <li key={i}><code>{h.placeholder}</code> — {h.description}</li>
                                ))}
                            </ul>
                        </details>
                    </div>

                    {/* API Args */}
                    <div className='section-group-accent' style={{ borderRadius: 6, padding: '10px 14px', marginTop: 10 }}>
                        <div className='d-flex justify-content-between align-items-center' style={{ marginBottom: 6 }}>
                            <span className='section-group-title' style={{ fontWeight: 600, fontSize: 13 }}>API Arguments</span>
                            {!readOnly && <ButtonBuilder label='+ Add' type='button' className='btn-outline-secondary btn-sm' useSpinner={false} onClick={addApiArg} />}
                        </div>
                        <p style={{ fontSize: 11, color: '#666', margin: '0 0 8px' }}>
                            Extra query parameters passed directly to the API (e.g., <code>include: requester</code>)
                        </p>
                        {apiArgs.map((arg, i) => (
                            <Row key={i} className='mb-2 align-items-center'>
                                <Col md={4}>
                                    <Input bsSize='sm' placeholder='Key (e.g., include)' value={arg.key} readOnly={readOnly}
                                        onChange={(e) => updateApiArg(i, 'key', e.target.value)} />
                                </Col>
                                <Col md={7}>
                                    <Input bsSize='sm' placeholder='Value (e.g., requester)' value={arg.value} readOnly={readOnly}
                                        onChange={(e) => updateApiArg(i, 'value', e.target.value)} />
                                </Col>
                                <Col md={1} className='d-flex align-items-center'>
                                    {!readOnly && <Button size='sm' color='link' className='text-danger p-0' onClick={() => removeApiArg(i)}>✕</Button>}
                                </Col>
                            </Row>
                        ))}
                        {apiArgs.length === 0 && <p style={{ fontSize: 12, color: '#888', margin: '4px 0' }}>No extra API arguments</p>}
                    </div>

                    {/* Fields */}
                    <div className='section-group-muted' style={{ borderRadius: 6, padding: '10px 14px', marginTop: 10 }}>
                        <span className='section-group-title' style={{ fontWeight: 600, fontSize: 13, display: 'block', marginBottom: 6 }}>Fields</span>
                        <Input placeholder='Comma-separated field names (blank = all fields)' value={fields} readOnly={readOnly}
                            onChange={(e) => setFields(e.target.value)} />
                        <p style={{ fontSize: 11, color: '#888', margin: '4px 0 0' }}>Leave empty to fetch all fields. The factory translates this per integration.</p>
                    </div>

                    {/* Options */}
                    <div className='section-group-muted' style={{ borderRadius: 6, padding: '10px 14px', marginTop: 10 }}>
                        <span className='section-group-title' style={{ fontWeight: 600, fontSize: 13, display: 'block', marginBottom: 6 }}>Options</span>
                        <Select
                            className='react-select-container'
                            classNamePrefix='react-select'
                            isMulti
                            value={options.map(o => ({ label: o, value: o }))}
                            options={[
                                { label: 'Clean Data (CWM: removes _info and href)', value: 'cleanData' },
                            ]}
                            isDisabled={readOnly}
                            onChange={(selected) => setOptions((selected || []).map(s => s.value))}
                            placeholder='Select options...'
                            isClearable
                            styles={selectStyles}
                            menuPortalTarget={document.body}
                            menuPosition='fixed'
                        />
                    </div>

                </Form>
            }
        />
    );
};

export default SyncFilterBuilder;
